import { getJwtToken } from '../config/cognito'
import { ApiError } from '../config/api'

// Mirror the trailing-slash strip applied in config/api.ts so paths that
// start with `/api/...` never produce a `//api/...` double-slash, regardless
// of how the env-sync pipeline formats `REACT_APP_API_URL`.
const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')

/**
 * Fetches the `wps_sections` blob for a pipe set together with the integer
 * `version` exposed by the server as an `ETag` header. We need both because
 * the PQR editable preview's Save & Generate flow uses optimistic
 * concurrency: the client sends the loaded `version` as `ifMatchVersion`,
 * and the server rejects the save with 409 if it has been bumped since.
 *
 * `apiFetch` in `config/api.ts` deliberately hides headers; rather than
 * widening that contract we hand-roll the fetch here for the one caller that
 * cares about both body and ETag.
 */
export interface SectionsWithVersion {
  sections: Record<string, unknown>
  version: number
}

export async function fetchSectionsWithVersion(
  projectId: string,
  pipeSetId: string
): Promise<SectionsWithVersion> {
  const token = await getJwtToken()
  if (!token) {
    throw new ApiError(401, 'Not authenticated')
  }

  const url = `${API_BASE_URL}/api/projects/${projectId}/pipe-sets/${pipeSetId}/sections`
  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({} as { error?: string }))
    throw new ApiError(res.status, body.error || res.statusText)
  }

  const sections = (await res.json()) as Record<string, unknown>
  return {
    sections: sections || {},
    version: parseEtagVersion(res)
  }
}

function parseEtagVersion(res: Response): number {
  const etag = res.headers.get('ETag') || res.headers.get('etag') || ''
  const versionStr = etag.replace(/^"|"$/g, '').trim()
  const version = Number.parseInt(versionStr, 10)
  return Number.isFinite(version) ? version : 0
}

export async function saveSectionsWithVersion(
  projectId: string,
  pipeSetId: string,
  sections: Record<string, unknown>,
  expectedVersion: number | null
): Promise<SectionsWithVersion> {
  const token = await getJwtToken()
  if (!token) {
    throw new ApiError(401, 'Not authenticated')
  }

  const url = `${API_BASE_URL}/api/projects/${projectId}/pipe-sets/${pipeSetId}/sections`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  }
  if (expectedVersion !== null && expectedVersion > 0) {
    headers['If-Match'] = String(expectedVersion)
  }

  const res = await fetch(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify(sections)
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({} as { error?: string }))
    throw new ApiError(res.status, body.error || res.statusText)
  }

  const saved = (await res.json()) as Record<string, unknown>
  return {
    sections: saved || {},
    version: parseEtagVersion(res)
  }
}
